import { useCallback, useEffect, useMemo, useState } from "react";
import { CheckCircle2, CreditCard, MapPin, MessageSquareText, RefreshCw, RotateCcw, Search, Wallet, XCircle } from "lucide-react";
import Swal from "../../utils/apparifySwal";

import Panel from "../components/Panel";
import { businessSwalBase } from "../utils/businessSwal";
import { businessApi } from "../../services/api";
import ReturnRequestEvidenceGallery from "../../components/orders/ReturnRequestEvidenceGallery";
import { normalizeReturnRequestMedia } from "../../utils/returnRequestMedia";
import { Button, PageHeader } from "../../components/ui";

const STATUS_FILTERS = ["ALL", "REQUESTED", "APPROVED", "REJECTED", "REFUNDED"];

function money(value) {
  return `₱ ${Number(value || 0).toLocaleString("en-PH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function safeDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return `${date.toLocaleDateString("en-PH", { year: "numeric", month: "short", day: "2-digit" })} • ${date.toLocaleTimeString("en-PH", { hour: "2-digit", minute: "2-digit" })}`;
}

function normalizeReturnRequest(returnRequest) {
  if (!returnRequest) return null;

  const evidence = normalizeReturnRequestMedia(
    returnRequest?.evidence || returnRequest?.media,
    returnRequest?.meta?.evidence || returnRequest?.meta?.media
  );

  return {
    id: returnRequest?.id ?? null,
    status: String(returnRequest?.status || returnRequest?.requestStatus || returnRequest?.request_status || "REQUESTED").trim().toUpperCase(),
    reason: returnRequest?.reason || null,
    details: returnRequest?.details || null,
    refundAmount: returnRequest?.refundAmount != null ? Number(returnRequest.refundAmount) : returnRequest?.refund_amount != null ? Number(returnRequest.refund_amount) : null,
    refundDestination: returnRequest?.refundDestination || returnRequest?.refund_destination || null,
    walletTransactionId: returnRequest?.walletTransactionId || returnRequest?.wallet_transaction_id || null,
    requestedAt: returnRequest?.requestedAt || returnRequest?.requested_at || returnRequest?.created_at || null,
    reviewedAt: returnRequest?.reviewedAt || returnRequest?.reviewed_at || null,
    refundedAt: returnRequest?.refundedAt || returnRequest?.refunded_at || null,
    resolutionNote: returnRequest?.resolutionNote || returnRequest?.resolution_note || null,
    evidence,
  };
}

function formatReturnAddress(address) {
  if (!address) return "";
  if (typeof address === "string") return address.trim();

  const nestedAddress = address?.address && typeof address.address === "object"
    ? address.address
    : null;
  const source = nestedAddress ? { ...address, ...nestedAddress } : address;
  const joinParts = (parts = []) => parts
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .join(", ");

  const fullAddress = joinParts([
    source?.fullAddress,
    source?.full_address,
    source?.formatted,
    typeof address?.address === "string" ? address.address : null,
  ]);
  if (fullAddress) return fullAddress;

  return joinParts([
    source?.line1 || source?.addressLine1 || source?.address_line1 || source?.streetAddress || source?.street_address || source?.street,
    joinParts([
      source?.line2 || source?.addressLine2 || source?.address_line2,
      source?.unit,
      source?.suite,
      source?.floor,
      source?.building,
      source?.subdivision,
      source?.district,
      source?.barangay,
      source?.landmark,
    ]),
    joinParts([
      source?.city || source?.cityMunicipality || source?.city_municipality || source?.municipality || source?.town,
      source?.province || source?.state,
      source?.region,
      source?.postalCode || source?.postal_code || source?.zipCode || source?.zip_code,
    ]),
  ]);
}

function normalizeOrder(raw, index) {
  const returnRequest = normalizeReturnRequest(raw?.returnRequest || raw?.return_request);
  if (!returnRequest) return null;

  return {
    id: raw?.id ?? `order-${index}`,
    reference: raw?.sellerOrderReference || raw?.seller_order_reference || raw?.orderReference || raw?.order_reference || null,
    customer: raw?.customer?.name || raw?.customerName || raw?.customer || raw?.user?.name || raw?.user?.email || raw?.email || "Shopper",
    email: raw?.customer?.email || raw?.customerEmail || raw?.email || raw?.user?.email || "",
    total: Number(raw?.total ?? raw?.grandTotal ?? raw?.amount ?? raw?.total_amount ?? 0) || 0,
    businessReturnAddress: raw?.businessReturnAddress || raw?.business_return_address || null,
    returnRequest,
  };
}

function getStatusLabel(status) {
  switch (String(status || "").trim().toUpperCase()) {
    case "REFUNDED":
      return "Refunded";
    case "APPROVED":
      return "Approved";
    case "REJECTED":
      return "Rejected";
    default:
      return "Pending Review";
  }
}

function chip(status) {
  const base = "inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-semibold tracking-wide";
  switch (String(status || "").trim().toUpperCase()) {
    case "REFUNDED":
      return `${base} border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/40 dark:bg-emerald-950/30 dark:text-emerald-200`;
    case "APPROVED":
      return `${base} border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200`;
    case "REJECTED":
      return `${base} border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900/40 dark:bg-rose-950/30 dark:text-rose-200`;
    default:
      return `${base} border-orange-200 bg-orange-50 text-orange-700 dark:border-orange-900/40 dark:bg-orange-950/30 dark:text-orange-200`;
  }
}

function refundAmount(order) {
  return Number(order?.returnRequest?.refundAmount || order?.total || 0);
}

export default function BusinessReturnRefunds() {
  const swalBase = useMemo(() => ({ ...businessSwalBase, target: "#business-swal-root" }), []);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [orders, setOrders] = useState([]);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [resolutionNote, setResolutionNote] = useState("");
  const [actionError, setActionError] = useState("");
  const [actionLoading, setActionLoading] = useState("");

  const loadRequests = useCallback(async ({ preserveSelection = true } = {}) => {
    setLoading(true);
    setError("");
    try {
      const payload = await businessApi.getOrders();
      const rows = Array.isArray(payload) ? payload : Array.isArray(payload?.data) ? payload.data : Array.isArray(payload?.items) ? payload.items : Array.isArray(payload?.data?.items) ? payload.data.items : [];
      const normalized = rows.map(normalizeOrder).filter(Boolean);
      setOrders(normalized);
      setSelectedOrderId((current) => preserveSelection && current && normalized.some((entry) => String(entry.id) === String(current)) ? current : normalized[0]?.id ?? null);
    } catch (err) {
      setOrders([]);
      setError(err?.message || "Unable to load return and refund requests right now.");
      setSelectedOrderId(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRequests({ preserveSelection: false });
  }, [loadRequests]);

  const filteredOrders = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return orders.filter((order) => {
      const statusMatches = statusFilter === "ALL" || order?.returnRequest?.status === statusFilter;
      if (!statusMatches) return false;
      if (!normalizedQuery) return true;
      return [order.reference, order.customer, order.email, order.returnRequest?.reason, order.returnRequest?.details].filter(Boolean).join(" ").toLowerCase().includes(normalizedQuery);
    });
  }, [orders, query, statusFilter]);

  const selectedOrder = useMemo(
    () => filteredOrders.find((order) => String(order.id) === String(selectedOrderId)) || filteredOrders[0] || null,
    [filteredOrders, selectedOrderId]
  );

  useEffect(() => {
    setResolutionNote(selectedOrder?.returnRequest?.resolutionNote || "");
    setActionError("");
  }, [selectedOrder]);

  const handleReject = async () => {
    if (!selectedOrder?.id) return;
    const trimmedNote = resolutionNote.trim();
    if (!trimmedNote) {
      setActionError("A rejection note is required so the shopper understands the decision.");
      return;
    }

    const confirm = await Swal.fire({
      ...swalBase,
      icon: "warning",
      title: "Reject this request?",
      html: `Reject the shopper request for <b>${selectedOrder.reference || `seller order #${selectedOrder.id}`}</b>?`,
      showCancelButton: true,
      confirmButtonText: "Reject Request",
      cancelButtonText: "Cancel",
    });

    if (!confirm.isConfirmed) return;

    setActionLoading("reject");
    setActionError("");
    try {
      await businessApi.rejectReturnRequest(selectedOrder.id, { resolutionNote: trimmedNote });
      await loadRequests();
      await Swal.fire({
        ...swalBase,
        icon: "success",
        title: "Request rejected",
        html: "The shopper request was updated successfully.",
        timer: 1600,
        showConfirmButton: false,
      });
    } catch (err) {
      setActionError(err?.message || "Unable to reject this request right now.");
    } finally {
      setActionLoading("");
    }
  };

  const handleApprove = async () => {
    if (!selectedOrder?.id) return;
    const confirm = await Swal.fire({
      ...swalBase,
      icon: "question",
      title: "Approve this request?",
      html: "This marks the request as approved. Refunding the shopper wallet remains a separate action.",
      showCancelButton: true,
      confirmButtonText: "Approve Request",
      cancelButtonText: "Cancel",
    });

    if (!confirm.isConfirmed) return;

    setActionLoading("approve");
    setActionError("");
    try {
      await businessApi.approveReturnRequest(selectedOrder.id, {
        resolutionNote: resolutionNote.trim() || "Approved. Follow the return instructions before refund is released.",
      });
      await loadRequests();
      await Swal.fire({
        ...swalBase,
        icon: "success",
        title: "Request approved",
        html: "The shopper can now follow the return instructions. Refund stays locked until you credit it.",
        timer: 1600,
        showConfirmButton: false,
      });
    } catch (err) {
      setActionError(err?.message || "Unable to approve this request right now.");
    } finally {
      setActionLoading("");
    }
  };

  const handleRefundToWallet = async () => {
    if (!selectedOrder?.id) return;
    const confirm = await Swal.fire({
      ...swalBase,
      icon: "question",
      title: "Credit shopper wallet?",
      html: `Credit <b>${money(refundAmount(selectedOrder))}</b> to the shopper wallet now? This should only be done after the approved return is ready for payout.`,
      showCancelButton: true,
      confirmButtonText: "Credit Wallet",
      cancelButtonText: "Cancel",
    });

    if (!confirm.isConfirmed) return;

    setActionLoading("refund");
    setActionError("");
    try {
      await businessApi.refundReturnRequestToWallet(selectedOrder.id, {
        resolutionNote: resolutionNote.trim() || "Refund credited to shopper wallet.",
      });
      await loadRequests();
      await Swal.fire({
        ...swalBase,
        icon: "success",
        title: "Wallet credited",
        html: "The shopper wallet has been credited successfully.",
        timer: 1600,
        showConfirmButton: false,
      });
    } catch (err) {
      setActionError(err?.message || "Unable to credit the shopper wallet right now.");
    } finally {
      setActionLoading("");
    }
  };

  const selectedRequestStatus = selectedOrder?.returnRequest?.status || "";
  const canApprove = selectedRequestStatus === "REQUESTED";
  const canReject = selectedRequestStatus === "REQUESTED";
  const canRefund = selectedRequestStatus === "APPROVED";
  const returnAddressText = formatReturnAddress(selectedOrder?.businessReturnAddress);

  return (
    <div className="space-y-5">
      <PageHeader
        title="Return requests"
        description={
          <>
            Review what the customer sent and approve or decline the return. Approving does not move any money: crediting the shopper's wallet is a separate step you take here once the refund is due.
            <span className="mt-1 block">Parcels a rider brings back to the store are received in Logistics, under Delivery.</span>
          </>
        }
        actions={
          <Button size="sm" icon={RefreshCw} loading={loading} onClick={() => loadRequests()}>
            Refresh
          </Button>
        }
      />

      {error ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900/40 dark:bg-rose-950/30 dark:text-rose-200">{error}</div>
      ) : null}

      <div className="grid gap-4 xl:grid-cols-[380px_minmax(0,1fr)]">
        <Panel title="Request Queue">
          <div className="space-y-3">
            <div className="relative">
              <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 dark:text-neutral-500" />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search order, shopper, or reason"
                className="w-full rounded-2xl border border-neutral-200 bg-white py-2.5 pl-9 pr-3 text-sm text-neutral-900 outline-none transition placeholder:text-neutral-400 focus:border-neutral-400 focus:ring-2 focus:ring-neutral-200 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100 dark:placeholder:text-neutral-500 dark:focus:border-neutral-600 dark:focus:ring-neutral-800"
              />
            </div>

            <div
              className="grid grid-cols-6 gap-2 sm:max-w-md xl:max-w-none"
              role="group"
              aria-label="Filter requests by status"
            >
              {STATUS_FILTERS.map((status, index) => (
                <button
                  key={status}
                  type="button"
                  onClick={() => setStatusFilter(status)}
                  aria-pressed={statusFilter === status}
                  className={[
                    index === 3 ? "col-span-2 col-start-2" : "col-span-2",
                    "min-h-control-sm w-full whitespace-nowrap rounded-full border px-2.5 text-meta font-semibold transition-colors max-md:min-h-touch",
                    statusFilter === status
                      ? "border-neutral-900 bg-neutral-900 text-white dark:border-neutral-100 dark:bg-neutral-100 dark:text-neutral-900"
                      : "border-neutral-200 bg-white text-neutral-600 hover:bg-neutral-50 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-300 dark:hover:bg-neutral-800",
                  ].join(" ")}
                >
                  {status === "ALL" ? "All" : getStatusLabel(status)}
                </button>
              ))}
            </div>

            {loading ? (
              <div className="space-y-3">
                {Array.from({ length: 4 }).map((_, index) => (
                  <div key={index} className="h-24 animate-pulse rounded-2xl bg-neutral-100 dark:bg-neutral-800" />
                ))}
              </div>
            ) : filteredOrders.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-neutral-300 bg-neutral-50 px-4 py-10 text-center dark:border-neutral-700 dark:bg-neutral-800/50">
                <MessageSquareText size={34} className="mx-auto text-neutral-300 dark:text-neutral-600" />
                <div className="mt-3 text-sm font-semibold text-neutral-900 dark:text-neutral-100">No matching requests</div>
              </div>
            ) : (
              filteredOrders.map((order) => (
                <button
                  key={order.id}
                  type="button"
                  onClick={() => setSelectedOrderId(order.id)}
                  className={[
                    "w-full rounded-2xl border px-4 py-3 text-left transition",
                    String(order.id) === String(selectedOrder?.id)
                      ? "border-neutral-900 bg-neutral-900 text-white shadow-sm dark:border-neutral-100 dark:bg-neutral-100 dark:text-neutral-900"
                      : "border-neutral-200 bg-white hover:border-neutral-300 hover:bg-neutral-50 dark:border-neutral-800 dark:bg-neutral-900 dark:hover:border-neutral-700 dark:hover:bg-neutral-800/60",
                  ].join(" ")}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold">{order.reference || `Seller order #${order.id}`}</div>
                      <div className="mt-1 text-xs opacity-75">{order.customer}</div>
                    </div>
                    <span className={String(order.id) === String(selectedOrder?.id) ? "inline-flex items-center rounded-full border border-white/15 bg-white/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-white dark:border-neutral-300 dark:bg-neutral-200 dark:text-neutral-900" : chip(order.returnRequest.status)}>
                      {getStatusLabel(order.returnRequest.status)}
                    </span>
                  </div>
                  <div className="mt-2 line-clamp-2 text-sm opacity-90">{order.returnRequest.reason || "No reason provided."}</div>
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] font-medium opacity-75">
                    <span className="inline-flex items-center gap-1 rounded-full border border-current/15 px-2.5 py-1">
                      <CreditCard size={11} />
                      {money(refundAmount(order))}
                    </span>
                    <span className="inline-flex items-center rounded-full border border-current/15 px-2.5 py-1">
                      {safeDateTime(order.returnRequest.requestedAt)}
                    </span>
                  </div>
                </button>
              ))
            )}
          </div>
        </Panel>

        <Panel title="Request Details" right={selectedOrder?.returnRequest ? <span className={chip(selectedOrder.returnRequest.status)}>{getStatusLabel(selectedOrder.returnRequest.status)}</span> : null}>
          {!selectedOrder ? (
            <div className="rounded-2xl border border-dashed border-neutral-300 bg-neutral-50 px-4 py-10 text-center dark:border-neutral-700 dark:bg-neutral-800/50">
              <RotateCcw size={34} className="mx-auto text-neutral-300 dark:text-neutral-600" />
              <div className="mt-3 text-sm font-semibold text-neutral-900 dark:text-neutral-100">Select a request</div>
            </div>
          ) : (
            <div className="space-y-4">
              <section className="rounded-2xl border border-neutral-200 bg-neutral-50/80 p-4 dark:border-neutral-800 dark:bg-neutral-800/40">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-neutral-500 dark:text-neutral-400">Seller Order</div>
                    <div className="mt-1 text-lg font-semibold text-neutral-900 dark:text-neutral-100">{selectedOrder.reference || `#${selectedOrder.id}`}</div>
                    <div className="mt-2 text-sm text-neutral-500 dark:text-neutral-400">{selectedOrder.customer}{selectedOrder.email ? ` • ${selectedOrder.email}` : ""}</div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <span className="inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-neutral-600 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-300">
                      <CreditCard size={11} />
                      Full refund {money(refundAmount(selectedOrder))}
                    </span>
                    {String(selectedOrder.returnRequest.refundDestination || "").toUpperCase() === "WALLET" ? (
                      <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-[11px] font-semibold text-emerald-700 dark:border-emerald-900/40 dark:bg-emerald-950/30 dark:text-emerald-200">
                        <Wallet size={11} />
                        Shopper wallet
                      </span>
                    ) : null}
                  </div>
                </div>

                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  <div className="rounded-2xl border border-neutral-200 bg-white px-3.5 py-3 dark:border-neutral-800 dark:bg-neutral-900">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-neutral-500 dark:text-neutral-400">Reason</div>
                    <div className="mt-1 text-sm leading-relaxed text-neutral-900 dark:text-neutral-100">{selectedOrder.returnRequest.reason || "No reason provided."}</div>
                  </div>
                  <div className="rounded-2xl border border-neutral-200 bg-white px-3.5 py-3 dark:border-neutral-800 dark:bg-neutral-900">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-neutral-500 dark:text-neutral-400">Details</div>
                    <div className="mt-1 text-sm leading-relaxed text-neutral-900 dark:text-neutral-100">{selectedOrder.returnRequest.details || "No extra details were provided."}</div>
                  </div>
                  <div className={returnAddressText ? "rounded-2xl border border-neutral-200 bg-white px-3.5 py-3 dark:border-neutral-800 dark:bg-neutral-900 sm:col-span-2" : "rounded-2xl border border-amber-200 bg-amber-50 px-3.5 py-3 text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200 sm:col-span-2"}>
                    <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-neutral-500 dark:text-neutral-400">
                      <MapPin size={11} />
                      Business Return Address On File
                    </div>
                    <div className={returnAddressText ? "mt-1 break-words text-sm leading-relaxed text-neutral-900 dark:text-neutral-100" : "mt-1 text-sm leading-relaxed"}>
                      {returnAddressText || "No complete business return address is on file for this order yet. Shopper instructions will require address confirmation through chat before shipping anything."}
                    </div>
                  </div>
                </div>

                {selectedOrder.returnRequest.evidence.length > 0 ? (
                  <ReturnRequestEvidenceGallery media={selectedOrder.returnRequest.evidence} className="mt-4" />
                ) : null}

                <div className="mt-4 flex flex-wrap gap-2 text-[11px] font-medium text-neutral-500 dark:text-neutral-400">
                  <span className="inline-flex items-center rounded-full border border-neutral-200 bg-white px-3 py-1.5 dark:border-neutral-700 dark:bg-neutral-900">Requested {safeDateTime(selectedOrder.returnRequest.requestedAt)}</span>
                  {selectedOrder.returnRequest.reviewedAt ? (
                    <span className="inline-flex items-center rounded-full border border-neutral-200 bg-white px-3 py-1.5 dark:border-neutral-700 dark:bg-neutral-900">Reviewed {safeDateTime(selectedOrder.returnRequest.reviewedAt)}</span>
                  ) : null}
                  {selectedOrder.returnRequest.refundedAt ? (
                    <span className="inline-flex items-center rounded-full border border-emerald-200 bg-white px-3 py-1.5 text-emerald-700 dark:border-emerald-900/40 dark:bg-emerald-950/30 dark:text-emerald-200">Refunded {safeDateTime(selectedOrder.returnRequest.refundedAt)}</span>
                  ) : null}
                </div>
              </section>

              <section className="rounded-2xl border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-900">
                <div className="flex items-center gap-2">
                  <MessageSquareText size={15} className="text-neutral-400 dark:text-neutral-500" />
                  <div className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">Resolution Note</div>
                </div>
                <textarea
                  value={resolutionNote}
                  onChange={(event) => {
                    setResolutionNote(event.target.value.slice(0, 500));
                    setActionError("");
                  }}
                  rows={4}
                  maxLength={500}
                  placeholder="Add a clear decision note for the shopper..."
                  className="mt-3 w-full rounded-2xl border border-neutral-200 bg-neutral-50 px-4 py-3 text-sm text-neutral-900 outline-none transition placeholder:text-neutral-400 focus:border-neutral-400 focus:ring-2 focus:ring-neutral-200 dark:border-neutral-700 dark:bg-neutral-950 dark:text-neutral-100 dark:placeholder:text-neutral-500 dark:focus:border-neutral-600 dark:focus:ring-neutral-800"
                />
                <div className="mt-2 flex items-center justify-between gap-3 text-xs text-neutral-400 dark:text-neutral-500">
                  <span>Saved with the request for shopper visibility.</span>
                  <span>{resolutionNote.trim().length}/500</span>
                </div>

                {selectedRequestStatus === "REFUNDED" || selectedOrder.returnRequest.refundedAt || selectedOrder.returnRequest.walletTransactionId ? (
                  <div className="mt-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700 dark:border-emerald-900/40 dark:bg-emerald-950/30 dark:text-emerald-200">
                    Refund has already been processed for this request.
                  </div>
                ) : null}

                {selectedRequestStatus === "REQUESTED" ? (
                  <div className="mt-3 rounded-2xl border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-700 dark:border-orange-900/40 dark:bg-orange-950/30 dark:text-orange-200">
                    Approve the request first. Wallet credit stays locked until the request is approved.
                  </div>
                ) : null}

                {selectedRequestStatus === "APPROVED" ? (
                  <div className="mt-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200">
                    This request is approved. Refund is still pending until you credit the shopper wallet.
                  </div>
                ) : null}

                {actionError ? (
                  <div className="mt-3 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900/40 dark:bg-rose-950/30 dark:text-rose-200">{actionError}</div>
                ) : null}

                <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-end">
                  {canApprove || actionLoading === "approve" ? (
                    <button
                      type="button"
                      onClick={handleApprove}
                      disabled={!canApprove || Boolean(actionLoading)}
                      className="inline-flex items-center justify-center gap-2 rounded-2xl border border-amber-300 bg-white px-4 py-3 text-sm font-semibold text-amber-700 transition hover:bg-amber-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-amber-900/40 dark:bg-neutral-900 dark:text-amber-300 dark:hover:bg-amber-950/20"
                    >
                      {actionLoading === "approve" ? <RefreshCw size={15} className="animate-spin" /> : <CheckCircle2 size={15} />}
                      {actionLoading === "approve" ? "Approving..." : "Approve Request"}
                    </button>
                  ) : null}
                  {canReject || actionLoading === "reject" ? (
                    <button
                      type="button"
                      onClick={handleReject}
                      disabled={!canReject || Boolean(actionLoading)}
                      className="inline-flex items-center justify-center gap-2 rounded-2xl border border-rose-300 bg-white px-4 py-3 text-sm font-semibold text-rose-700 transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-rose-900/40 dark:bg-neutral-900 dark:text-rose-300 dark:hover:bg-rose-950/20"
                    >
                      <XCircle size={15} />
                      {actionLoading === "reject" ? "Rejecting..." : "Reject Request"}
                    </button>
                  ) : null}
                  {canRefund || actionLoading === "refund" ? (
                    <button
                      type="button"
                      onClick={handleRefundToWallet}
                      disabled={!canRefund || Boolean(actionLoading)}
                      className="business-primary-action inline-flex items-center justify-center gap-2 rounded-2xl bg-neutral-900 px-4 py-3 text-sm font-semibold text-white transition hover:bg-neutral-800 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-neutral-100 dark:text-neutral-900 dark:hover:bg-neutral-200"
                    >
                      {actionLoading === "refund" ? <RefreshCw size={15} className="animate-spin" /> : <Wallet size={15} />}
                      {actionLoading === "refund" ? "Crediting Wallet..." : "Credit Shopper Wallet"}
                    </button>
                  ) : null}
                </div>
              </section>
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}
